import { inject, provide, type InjectionKey } from 'vue';

/**
 * Product-agnostic analytics sink. Libraries emit semantic events through this
 * interface; the consuming app provides the concrete implementation (e.g. Mixpanel), so no
 * analytics vendor dependency leaks into shared libs.
 */
export interface AnalyticsTracker {
  track(event: string, properties?: Record<string, unknown>): void;
}

export const ANALYTICS_TRACKER_KEY: InjectionKey<AnalyticsTracker> = Symbol('infuse-analytics-tracker');

// When no product provides a tracker (e.g. in tests, or an app without analytics)
// tracking is a silent no-op.
const NOOP_TRACKER: AnalyticsTracker = { track: () => undefined };

/** Provide an analytics implementation from within a component setup. */
export function provideAnalyticsTracker(tracker: AnalyticsTracker): void {
  provide(ANALYTICS_TRACKER_KEY, tracker);
}

/** Inject the analytics tracker; returns a no-op when nothing is provided. */
export function useAnalyticsTracker(): AnalyticsTracker {
  return inject(ANALYTICS_TRACKER_KEY, NOOP_TRACKER);
}
